import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { X, Mail, Gift, ArrowRight, Sparkles, Copy, Check, Share2, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const STORAGE_KEY = 'ailedger_email_captured';
const DISMISS_KEY = 'ailedger_popup_dismissed';
const REFERRAL_KEY = 'ailedger_referral_code';
const SITE_URL = 'https://theailedger.ai';

export default function EmailCapturePopup() {
  const [visible, setVisible] = useState(false);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [referralCount, setReferralCount] = useState(0);
  const [wasReferred, setWasReferred] = useState(false);
  const [copied, setCopied] = useState(false);
  const exitHandlerRef = useRef(null);
  const timerRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const refFromUrl = new URLSearchParams(window.location.search).get('ref') || '';

  // Determine if the popup should be allowed on the current page
  const isAllowedPage = location.pathname === '/' || location.pathname === '/coming-soon';

  // Check if the current user is admin/creator/seller (they browse the marketplace, don't need popups)
  const isPrivilegedUser = (() => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || 'null');
      if (!user) return false;
      const role = (user.role || '').toLowerCase();
      return role === 'admin' || role === 'creator' || role === 'seller';
    } catch { return false; }
  })();

  useEffect(() => {
    // Gate: only show on homepage / coming-soon AND not for admin/creator/seller
    if (!isAllowedPage || isPrivilegedUser) return;
    if (localStorage.getItem(STORAGE_KEY)) return;

    const dismissed = localStorage.getItem(DISMISS_KEY);
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      if (Date.now() - dismissedAt < 3 * 24 * 60 * 60 * 1000) return;
    }

    timerRef.current = setTimeout(() => {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    }, 8000);

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !localStorage.getItem(STORAGE_KEY)) setVisible(true);
    };
    exitHandlerRef.current = handleMouseLeave;
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (exitHandlerRef.current) document.removeEventListener('mouseleave', exitHandlerRef.current);
    };
  }, [isAllowedPage, isPrivilegedUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const payload = { email: email.trim() };
      if (refFromUrl) payload.ref = refFromUrl;
      const res = await axios.post('/email-capture', payload);
      setSuccess(true);
      setDiscountCode(res.data.discount_code || 'EARLY10');
      setReferralCode(res.data.referral_code || '');
      setReferralCount(res.data.referral_count || 0);
      setWasReferred(!!res.data.was_referred);
      if (res.data.referral_code) localStorage.setItem(REFERRAL_KEY, res.data.referral_code);
      localStorage.setItem(STORAGE_KEY, 'true');
      toast.success("You're on the list!");
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
  };

  const referralLink = referralCode ? `${SITE_URL}?ref=${referralCode}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareTwitter = () => {
    const text = encodeURIComponent("I just joined the waitlist for The AI Ledger \u2014 a curated marketplace for AI creators. Join with my link and we both get 15% off!");
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${encodeURIComponent(referralLink)}`, '_blank');
  };

  const shareWhatsApp = () => {
    const text = encodeURIComponent(`Hey! I found this curated AI marketplace \u2014 0% fees for founding creators. Use my link and we both get 15% off: ${referralLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const shareEmail = () => {
    const subject = encodeURIComponent('Get 15% off AI tools \u2014 The AI Ledger waitlist');
    const body = encodeURIComponent(`Hey,\n\nI joined the waitlist for The AI Ledger, a curated marketplace for AI creators launching soon.\n\nIf you join through my link, we both get 15% off our first purchase:\n${referralLink}\n\nCheck it out!`);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  const discountPct = discountCode.startsWith('EARLY15') ? 15 : 10;

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="email-capture-popup">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleDismiss} />

      <div className="relative w-full max-w-md rounded-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300 max-h-[90vh] overflow-y-auto" style={{ background: 'linear-gradient(135deg, #0a0a0a 0%, #1a0a2e 50%, #0a1628 100%)' }}>
        <button onClick={handleDismiss} className="absolute top-4 right-4 text-neutral-400 hover:text-white transition-colors z-10" data-testid="popup-close-btn">
          <X className="w-5 h-5" />
        </button>

        <div className="absolute top-0 right-0 w-48 h-48 bg-[#00F0FF]/10 rounded-full blur-[80px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-36 h-36 bg-white text-black/10 rounded-full blur-[60px] pointer-events-none" />

        <div className="relative z-10 p-8">
          {!success ? (
            <>
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/20 mb-5 mx-auto">
                <Gift className="w-7 h-7 text-[#00F0FF]" />
              </div>

              <h2 className="text-2xl font-bold text-white text-center mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {refFromUrl ? 'Your Friend Sent You 15% Off' : 'Wait — leaving so soon?'}
              </h2>
              <p className="text-neutral-400 text-center text-sm mb-6 leading-relaxed">
                {refFromUrl
                  ? "Use your friend\u2019s link and you BOTH get 15% off your first purchase on The AI Ledger."
                  : 'Get 10% off your first purchase on The AI Ledger \u2014 a curated marketplace for AI tools that actually work.'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-500" />
                  <Input
                    type="email"
                    required
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 bg-white/5 border-white/10 focus:border-[#00F0FF]/50 text-white placeholder:text-neutral-500 h-12 rounded-xl"
                    data-testid="popup-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#00F0FF] hover:bg-[#00D4E0] text-black font-bold rounded-xl h-12 text-base gap-2" data-testid="popup-submit-btn">
                  {loading ? 'Saving...' : (<>{refFromUrl ? 'Claim My 15% Off' : 'Get My 10% Off Code'}<ArrowRight className="w-4 h-4" /></>)}
                </Button>
              </form>

              <p className="text-neutral-500 text-xs text-center mt-4">No spam. Unsubscribe anytime.</p>
            </>
          ) : (
            <div className="text-center py-2">
              <div className="flex items-center justify-center w-14 h-14 rounded-xl bg-[#00F0FF]/10 border border-[#00F0FF]/20 mb-5 mx-auto">
                <Sparkles className="w-7 h-7 text-[#00F0FF]" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Your discount is ready!
              </h2>

              {wasReferred && (
                <div className="bg-white text-black/10 border border-white/30 rounded-xl px-4 py-3 mb-4">
                  <p className="text-white text-xs font-bold mb-0.5">Referral Bonus Unlocked</p>
                  <p className="text-neutral-300 text-xs">You and your friend both get 15% off!</p>
                </div>
              )}

              <div className="bg-white/5 border border-[#00F0FF]/30 rounded-xl p-5 mb-5">
                <p className="text-neutral-500 text-xs uppercase tracking-widest mb-1.5">Your Code</p>
                <p className="text-2xl font-bold text-[#00F0FF] tracking-wider" data-testid="popup-discount-code">{discountCode}</p>
                <p className="text-neutral-400 text-xs mt-1.5">{discountPct}% off your first purchase</p>
              </div>

              {referralCode && (
                <div className="bg-white/5 border border-white/20 rounded-xl p-5 mb-5 text-left">
                  <div className="flex items-center gap-2 mb-3">
                    <Share2 className="w-4 h-4 text-white" />
                    <p className="text-white text-sm font-bold">
                      {discountPct < 15 ? 'Want 15% off instead?' : 'Share & earn more rewards'}
                    </p>
                  </div>
                  <p className="text-neutral-400 text-xs mb-4 leading-relaxed">
                    Share your link with friends. When they join, you BOTH get upgraded to 15% off.
                  </p>

                  <div className="flex gap-2 mb-4">
                    <div className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs text-neutral-300 truncate" data-testid="popup-referral-link">
                      {referralLink}
                    </div>
                    <Button variant="outline" size="sm" onClick={copyLink} className="border-white/10 hover:bg-white/10 text-white rounded-lg px-3 shrink-0" data-testid="popup-copy-btn">
                      {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>

                  <div className="flex gap-2">
                    <button onClick={shareTwitter} className="flex-1 flex items-center justify-center gap-1.5 bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border border-[#1DA1F2]/20 rounded-lg py-2 text-[#1DA1F2] text-xs font-medium transition-colors" data-testid="popup-share-twitter">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                      Post
                    </button>
                    <button onClick={shareWhatsApp} className="flex-1 flex items-center justify-center gap-1.5 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/20 rounded-lg py-2 text-[#25D366] text-xs font-medium transition-colors" data-testid="popup-share-whatsapp">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Send
                    </button>
                    <button onClick={shareEmail} className="flex-1 flex items-center justify-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg py-2 text-neutral-300 text-xs font-medium transition-colors" data-testid="popup-share-email">
                      <Mail className="w-3.5 h-3.5" />
                      Email
                    </button>
                  </div>

                  {referralCount > 0 && (
                    <div className="flex items-center justify-center gap-1.5 mt-3 pt-3 border-t border-white/5">
                      <Users className="w-3.5 h-3.5 text-white" />
                      <p className="text-neutral-400 text-xs" data-testid="popup-referral-count">
                        <span className="text-white font-bold">{referralCount}</span> friend{referralCount !== 1 ? 's' : ''} joined through your link
                      </p>
                    </div>
                  )}
                </div>
              )}

              <Button
                onClick={() => {
                  // Pre-fill the signup form with their email so creating
                  // an account is one extra step (password only). We
                  // route through /?signup=1&email=... — Home.js reads
                  // those params on mount and opens the AuthModal on
                  // its "register" tab with the email pre-filled.
                  // handleDismiss closes the popup as we navigate.
                  handleDismiss();
                  navigate(`/?signup=1&email=${encodeURIComponent(email)}&from=popup`);
                }}
                className="w-full bg-[#00F0FF] hover:bg-[#00D4E0] text-black font-bold rounded-xl h-12 text-base gap-2"
                data-testid="popup-create-profile-btn"
              >
                Create my buyer profile <ArrowRight className="w-4 h-4" />
              </Button>
              <p className="text-neutral-500 text-xs text-center mt-3">
                Save your {discountPct}% code to your account so it auto-applies at checkout.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
